import { useState, useCallback } from 'react';
import { Mail, Check, AlertCircle, Loader2 } from 'lucide-react';
import { API } from '@/lib/constants';

export default function SubscribeForm() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API}/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message);
        setEmail('');
        setName('');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Invalid email address');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  }, [email, name]);

  return (
    <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-5" data-testid="subscribe-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#3498DB]/15">
          <Mail className="h-4 w-4 text-[#3498DB]" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Newsletter & F1 Race Reports</h3>
          <p className="text-[11px] text-white/40">
            Get our newsletter, session results, ESG leaders & standings after each GP
            <span className="ml-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#38bdf8]/35" data-testid="subscribe-a2-tag">via A2 Intelligence</span>
          </p>
        </div>
      </div>

      {status === 'success' ? (
        <div className="flex items-center gap-2 rounded-xl border border-[#2A9D8F]/30 bg-[#2A9D8F]/10 px-4 py-3" data-testid="subscribe-success">
          <Check className="h-4 w-4 text-[#2A9D8F]" />
          <p className="text-sm text-[#2A9D8F]">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="subscribe-name-input"
              className="w-full rounded-xl border border-white/10 bg-[#0B1E31] px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-[#3498DB]/40 focus:outline-none sm:w-1/3"
            />
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
              required
              data-testid="subscribe-email-input"
              className="flex-1 rounded-xl border border-white/10 bg-[#0B1E31] px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-[#3498DB]/40 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading' || !email}
            data-testid="subscribe-submit-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#3498DB] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#2e8cc0] disabled:opacity-50"
          >
            {status === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Subscribe to Newsletter & F1 Reports
          </button>
          {status === 'error' ? (
            <div className="flex items-center gap-2 text-xs text-[#E74C3C]" data-testid="subscribe-error">
              <AlertCircle className="h-3 w-3" />
              {message}
            </div>
          ) : null}
        </form>
      )}
    </div>
  );
}
